// l10n status: done
import { action, computed, flow, observable, makeObservable, runInAction } from "mobx"
import { PageAbstract } from "./PageAbstract"
import { rootStore } from "store/RootStore"
import { urlFixHttpProtocolMismatch } from "store/util/urlFixHttpProtocolMismatch"
import { appFeatures } from "AppFeatures"
import { eventType, endTs, startTs } from "utils/EventHelpers"
import { analyticsEvent, analyticsScreen, analyticsMeta, analyticsProgramDetailsSource } from "store/qlapi/LogService"
import { l10n } from "store/lang/L10n"

export class Search extends PageAbstract {
	gradientImg = `linear-gradient(
		90deg,hsl(0,0%,18.82%) 0%,
    hsla(0,0%,18.82%,0.9854227405) 8.62%,
    hsla(0,0%,18.82%,0.944606414) 16.56%,
    hsla(0,0%,18.82%,0.8819241983) 23.93%,
    hsla(0,0%,18.82%,0.8017492711) 30.85%,
    hsla(0,0%,18.82%,0.7084548104999999) 37.42%,
    hsla(0,0%,18.82%,0.6064139942) 43.77%,
    hsla(0,0%,18.82%,0.5) 50%,
    hsla(0,0%,18.82%,0.3935860058) 56.23%,
    hsla(0,0%,18.82%,0.29154518949999997) 62.58%,
    hsla(0,0%,18.82%,0.19825072889999995) 69.15%,
    hsla(0,0%,18.82%,0.11807580169999998) 76.07%,
    hsla(0,0%,18.82%,0.055393585999999995) 83.44%,
    hsla(0,0%,18.82%,0.014577259499999995) 91.38%,
    hsla(0,0%,18.82%,0) 100%) `

	backGroundImgStyle = {
		backgroundImage: null,
		backgroundPosition: "center",
		backgroundRepeat: "no-repeat",
		backgroundSize: "cover",
		// MTVW-624: overlap left to make poster bigger
		maxWidth: "300px"
	}

	results = null
	overview = null
	actor = null
	genre = null
	actorInGenre = null
	genreInGenre = null
	series = null
	categorySelected = null
	episodeSelectedId = null
	scrollposSelected = null
	refresh = false
	poppedFilterEventType = null
	//lastSearch = null
	_intervalId = null
	msEpg = rootStore.page.MsEpg

	CATEGORY_TOP = "top"
	CATEGORY_RECORDINGS = "recordings"
	CATEGORY_EVENTS = "events"
	CATEGORY_MOVIES = "movies"
	CATEGORY_SERIES = "series"
	CATEGORY_EPISODES = "episodes"
	CATEGORY_SPORTS = "sports"
	CATEGORY_ACTORS = "actors"
	CATEGORY_GENRES = "genres"
	CATEGORY_CHANNELS = "channels"
	//CATEGORY_PROGRAMS = 'programs'

	// SearchMatch types
	TYPE_EVENT = "event"
	TYPE_SERIE = "series"
	TYPE_GENRE = "genre"
	TYPE_CREDIT = "credits"
	TYPE_CHANNEL = "channel"

	constructor(parent, path) {
		super(parent, path)
		this.setupLanguageStrings()
		makeObservable(this, {
			results: observable,
			overview: observable,
			actor: observable,
			genre: observable,
			actorInGenre: observable,
			genreInGenre: observable,
			series: observable,
			categorySelected: observable,
			seasonSelected: observable,
			episodeSelectedId: observable,
			genresSelected: observable,
			scrollposSelected: observable,
			refresh: observable,
			filterEventType: observable,
			poppedFilterEventType: observable,
			// below would create a cyclic dependency
			//isReady: computed,
			repoReady: computed,
			setPoppedFilterEventType: action,
			setCategorySelected: action,
			setRefresh: action,
			searchOverview: action,
			// UNUSED
			//search: action,
			_init: action,
			setSeasonSelected: action,
			setEpisodeSelectedId: action,
			setFilterEventType: action,
			setGenresSelected: action,
			setScrollposSelected: action,
			setupLanguageStrings: action
		})
	}

	setupLanguageStrings() {
		this.ALL_SEASON_SELECTED = l10n.featureSearchAllEpisodes
		//TODO: check if needed a category for the all genres
		this.ALL_GENRES_SELECTED = l10n.featureSearchFilterCategoryAll
		this.FILTER_EVENT_ALL = l10n.featureSearchFilterCategoryAll
		this.FILTER_EVENT_LIVE_REPLAY = l10n.featureSearchTimeFilterLiveAndReplay
		this.FILTER_EVENT_UPCOMING = l10n.featureSearchTimeFilterUpcoming
		// MTVW-1016
		this.FILTER_EVENT_RECORDINGS = l10n.featureSearchTimeFilterRecordings
		// MTVW-1016 item 4, poppedFilterEventType
		this.FILTER_EVENT_KEEP = "keep"	// no translation, internal use only

		this.seasonSelected = this.ALL_SEASON_SELECTED
		this.genresSelected = this.ALL_GENRES_SELECTED
		// MTVW-620: FILTER_EVENT_ALL -> FILTER_EVENT_LIVE_REPLAY
		this.filterEventType = this.FILTER_EVENT_LIVE_REPLAY

		this.filterLabel = {
			[this.CATEGORY_TOP]: l10n.featureDetailsFilterCategoryTopResults,
			[this.CATEGORY_RECORDINGS]: l10n.featureSearchRecordings,
			[this.CATEGORY_SERIES]: l10n.featureSearchFilterCategorySeries,
			[this.CATEGORY_MOVIES]: l10n.featureSearchFilterCategoryMovies,
			[this.CATEGORY_SPORTS]: l10n.featureSearchFilterCategorySports,
			[this.CATEGORY_ACTORS]: l10n.featureSearchFilterCategoryActors,
			[this.CATEGORY_GENRES]: l10n.featureSearchFilterCategoryGenres,
			[this.CATEGORY_CHANNELS]: l10n.featureSearchFilterCategoryChannels,
			// [PROGRAMS]: 'Programs'
		}
	}

	isReplayAllowed = (event, maxReplayDuration = null) => {
		let allowed = rootStore.page.MsEpg?.isReplayAllowed(event?.channelId, startTs(event), endTs(event), false, maxReplayDuration)
		if (rootStore.page.Recordings.Manager.getMarker(event?.id)?.isComplete) allowed = true
		return allowed
	}

	get repoReady() {
		// MTVW-508: rootStore.page.MsEpg.channels, use channels MS
		return this.msEpg.channels
	}

	get isReady() {
		return this.repoReady && (this.results !== null)
	}

	get isOverViewReady() {
		return this.repoReady && (this.overview !== null)
	}

	getChannel(channelId) {
		return rootStore.channelsService.getChannel(channelId)
	}

	isChannelSubscribed(channelId) {
		return this.getChannel(channelId) !== null
	}

	// MTVW-631
	isOneChannelSubscribed(channels) {
		// temporarily true for environments that don't return channels yet
		let subscribed = channels?.length > 0 ? false : true
		for (let i = 0; i < channels?.length; i++) {
			subscribed ||= this.isChannelSubscribed(channels[i])
		}
		return subscribed
	}

	// MTVW-631
	isSearchMatchSubscribed(item) {
		switch (item.type) {
			case "event":
				return this.isChannelSubscribed(item.data.event?.channelId)
			case "series":
				return this.isOneChannelSubscribed(item.data.channels)
			case "credits":
				return this.isOneChannelSubscribed(item.data.channels)
			case "channel":
				return this.isChannelSubscribed(item.data.id)
			case "genre":
				return true
			default:
				return true
		}
	}

	// MTVW-1016 item 4, poppedFilterEventType
	setPoppedFilterEventType(filterEventType) {
		if (filterEventType === this.FILTER_EVENT_KEEP) this.poppedFilterEventType = this.filterEventType
		else this.poppedFilterEventType = filterEventType
	}

	setCategorySelected(value) {
		//console.debug("setCategorySelected", value)
		this.categorySelected = value
	}

	setSeasonSelected(value) {
		//console.trace("setSeasonSelected", value)
		this.seasonSelected = value
	}

	setEpisodeSelectedId(value) {
		//console.debug("setEpisodeSelectedId", value)
		this.episodeSelectedId = value
	}

	setGenresSelected(value) {
		//console.trace("setGenresSelected", value)
		this.genresSelected = value
	}

	// MTVW-1045
	seriesFilterAnalyticsMapping = new Map([
			[this.FILTER_EVENT_LIVE_REPLAY, analyticsEvent.replayFilter],
			[this.FILTER_EVENT_RECORDINGS, analyticsEvent.recordingsFilter],
			[this.FILTER_EVENT_UPCOMING, analyticsEvent.upcomingFilter],
			[this.FILTER_EVENT_ALL, analyticsEvent.allFilter]
		])
	setFilterEventType(value, isTapEvent = false) {
		this.filterEventType = value
		if (isTapEvent) {
			const activityStack = rootStore.page.OverlayEventDetails.activityStack
			let source = activityStack.length > 0 ? activityStack[activityStack.length-1].type : "search"
			if (source === "related") source = "seriesRelatedPrograms"
			//console.debug("filterType setFilterEventType", value, isTapEvent, this.seriesFilterAnalyticsMapping.get(value), this.series?.title, activityStack, source)
			// MTVW-1045
			/*if (source !== search)*/ rootStore.logService.analyticsTapEvent(analyticsEvent[this.seriesFilterAnalyticsMapping.get(value)], { [analyticsMeta.seriesName]: this.series?.title , [analyticsMeta.source]: source })
		}
	}

	setScrollposSelected(value) {
		this.scrollposSelected = value
	}

	applyTabFilter(items, filterEventType = this.FILTER_EVENT_LIVE_REPLAY) {
		if (!items) return null
		//console.debug("applyTabFilter items=", filterEventType, items)
		let result = items.map((item, index) => {
			if (filterEventType === this.FILTER_EVENT_LIVE_REPLAY) {
				//console.debug("applyTabFilter", filterEventType, item.source, item, index)
				if (eventType(item?.event) === "p" || eventType(item?.event) === "c") {
					//console.debug("filterType p or c")
					// MTVW-624: added check for out of replay window
					//if (!this.isReplayAllowed(item?.event)) console.debug("not allowed", item?.event)
					// MTVW-631: show items within 7 days, override maxReplayDuration
					// MTVW-1016: source "search-ms" to filter out old recordings,
					// compare to "traxis" instead of "search-ms" becaue source might be undefined!
					//console.debug("FILTER_EVENT_LIVE_REPLAY", this.isReplayAllowed(item?.event, "P7D") && item.source !== "traxis" ? item : null, item)
					return this.isReplayAllowed(item?.event, "P7D") && item.source !== "traxis" ? item : null
					// MTVW-629: don't filter anymore
					//return item
				} else {
					//console.debug("filterType null")
					return null
				}
			} else if (filterEventType === this.FILTER_EVENT_UPCOMING) {
				if (eventType(item?.event) === "n") {
					// MTVW-631: show items within 7 days, override maxReplayDuration
					//console.debug("FILTER_EVENT_UPCOMING", this.isReplayAllowed(item?.event, "P7D") ? item : null)
					return this.isReplayAllowed(item?.event, "P7D") ? item : null
					// MTVW-629: don't filter anymore
					//return item
				} else {
					return null
				}
			} else if (filterEventType === this.FILTER_EVENT_RECORDINGS) {
				//console.debug("applyTabFilter FILTER_EVENT_RECORDINGS", item.source, item?.event?.id, item, rootStore.page.Recordings.Manager.getMarker(item?.event?.id))
				// MTVW-1016
				const isShown = rootStore.page.Recordings.Manager.getMarker(item?.event?.id)?.isComplete
				//const isShown = rootStore.page.Recordings.Manager.getMarker(item?.event?.id ?? item?.data?.event?.id)
				//console.debug("FILTER_EVENT_RECORDINGS", item.type, isShown, item.content.Season, item.event.start, item.content.title, item.content.episodeName, item?.event?.id ,item )
				if (item.duplicate || item.ignore) console.debug("FILTER_EVENT_RECORDINGS duplicate", item)
				if (item.duplicate || item.ignore) return null
				//console.debug("FILTER_EVENT_RECORDINGS", isShown ? item : null)
				return isShown ? item : null
			} else if (filterEventType === this.FILTER_EVENT_ALL) {
				// MTVW-631: show items within 7 days, override maxReplayDuration
				// MTVW-1016: show also recordings
				//console.debug("applyTabFilter", filterEventType, item.source, item, index)
				if (item.duplicate) console.debug("FILTER_EVENT_ALL", item)
				if (item.duplicate || item.ignore) return null
				//console.debug("FILTER_EVENT_ALL", this.isReplayAllowed(item?.event, "P7D") ? item : null)
				return this.isReplayAllowed(item?.event, "P7D") ? item : null
				// MTVW-629: don't filter anymore
				//return item
			}
		})
		//result = result.every((element) => element === null) ? null : result
		// MTVW-624: remove 'null' elements and sort
		//console.debug("result=", result)
		if (result) {
			result = result.filter((element) => {
				return element != null
			})
			// MTVW-976: add || filterEventType === this.FILTER_EVENT_ALL
			if (filterEventType === this.FILTER_EVENT_UPCOMING || filterEventType === this.FILTER_EVENT_ALL) {
				result = result.sort((a, b) => {
					//console.debug("a %o, b %o", startTs(a?.event), startTs(b?.event))
					return startTs(a?.event) - startTs(b?.event)
				})
			} else {
				result = result.sort((a, b) => {
					//console.debug("a %o, b %o", startTs(a?.event), startTs(b?.event))
					return startTs(b?.event) - startTs(a?.event)
				})
			}
		}
		//console.debug("returning result=", result, result.length, filterEventType)
		return result?.length > 0 ? result : null
	}

	getChannelName(channelId) {
		const channel = this.getChannel(channelId)
		if (!channel) return null
		return channel.Name
	}

	getChannelLogo(channelId) {
		const channel = this.getChannel(channelId)
		if (!channel) return null
		// make sure to return secure url
		return urlFixHttpProtocolMismatch(channel.logoUrl)
	}

	setRefresh(value) {
		this.refresh = value
	}

	filterChannels(itemName, item, conditionCallback) {
		let filtered = []
		if (item?.length > 0) {
			filtered = item.filter(i => { return conditionCallback(i)/*this.isChannelSubscribed(i.id)*/ })
		}
		//console.debug(`# search ${itemName}: %s, filtered: %s, unfiltered %o, filtered %o`, item?.length, filtered?.length, item, filtered)
		return filtered
	}

	// not called anymore
	dedupRecordings() {
		const ownRecs = new Map()
		const myProfiledId = rootStore?.sso?.profile?.id
		console.debug("fixupOverview", rootStore.sso.getProfile(myProfiledId)?.name)

		// check if we need to append recordings to movies or sports for FILTER_EVENT_ALL
		if (this.overview[this.CATEGORY_RECORDINGS]?.length > 0) {
			// merge recordings into movies and sports so that they are shown when FILTER_EVENT_ALL is selected
			for (let i = 0; i < this.overview[this.CATEGORY_RECORDINGS]?.length; i++) {
				const item = this.overview[this.CATEGORY_RECORDINGS][i]
				if (item.type === "event") {
					// mark own recorings
					if (item.data.recordingStates?.[0]?.profileId === myProfiledId) ownRecs.set(item.data.event.id, true)

					//console.debug("recording=", this.overview[this.CATEGORY_RECORDINGS][i])
					if (!this.overview[this.CATEGORY_RECORDINGS][i].ignore) {
						if (this.overview[this.CATEGORY_MOVIES]?.length > 0) {
							this.overview[this.CATEGORY_MOVIES].push(item.data)	
						}
						if (this.overview[this.CATEGORY_SPORTS]?.length > 0) {
							this.overview[this.CATEGORY_SPORTS].push(item.data)
						}
					}
				}
				//else console.debug("ignoring duplicate", i, this.overview[this.CATEGORY_RECORDINGS][i])
			}

			console.debug("dedup own recordings", ownRecs)
			// remove duplicates
			if (this.overview[this.CATEGORY_MOVIES]?.length > 0) {
				const dedup = new Map()
				for (let i = 0; i < this.overview[this.CATEGORY_MOVIES]?.length; i++) {
					const item = this.overview[this.CATEGORY_MOVIES][i]
					if (!dedup.has(item.event.id))
						dedup.set(item.event.id, [{evId: item.event.id, ix: i, recProfId: item.recordingStates?.[0]?.profileId, nick: rootStore.sso.getProfile(item.recordingStates?.[0]?.profileId)?.name, start: item.event.start, startTs: startTs(item.event)}])
					else dedup.get(item.event.id).push({evId: item.event.id, ix: i, recProfId: item.recordingStates?.[0]?.profileId, nick: rootStore.sso.getProfile(item.recordingStates?.[0]?.profileId)?.name, start: item.event.start, startTs: startTs(item.event)})
				}

				dedup.forEach((values, key) => {
					if (values?.length > 1) {
						console.debug("dedup values", values, key)
						/*
						if (!values[0].recProfId) {
							console.debug("dedup marking", values[0].ix)
							//this.overview[this.CATEGORY_MOVIES].splice(values[0].ix, 1)
							this.overview[this.CATEGORY_MOVIES][values[0].ix].duplicate = true
						}
						*/
						for (let i = 0; i < values?.length; i++) {
							console.debug("dedup check", values[i].recProfId, myProfiledId, ownRecs.has(values[i].evId))
							if (!values[i].recProfId || (values[i].recProfId !== myProfiledId && ownRecs.has(values[i].evId))) {
								console.debug("dedup marking", values[i].ix, values[i].recProfId)
								this.overview[this.CATEGORY_MOVIES][values[i].ix].duplicate = true
							}
							else this.overview[this.CATEGORY_MOVIES][values[i].ix].duplicate = false
						}
					}
				})
				console.debug("movies after dedup", dedup, this.overview[this.CATEGORY_MOVIES])
			}
		}
	}

	fixupOverview() {
		if (!this.overview) return
		// if we have only recordings, show them as top results. This prevents also the NotFound error 
		// TODO: remove?
		//if (this.overview[this.CATEGORY_TOP]?.length === 0 && this.overview[this.CATEGORY_RECORDINGS]?.length > 0) this.overview[this.CATEGORY_TOP] = this.overview[this.CATEGORY_RECORDINGS]
		
		//this.dedupRecordings()

		// sort descending by eventCount
		//if (this.overview?.top) this.overview.top.sort((a, b) => (a.data.eventCount > b.data.eventCount) ? -1 : ((b.data.eventCount > a.data.eventCount) ? 1 : 0))
		// consider only subscribed channels
		this.overview.top = this.filterChannels("top", this.overview.top, i => this.isSearchMatchSubscribed(i))
		this.overview.series = this.filterChannels("series", this.overview.series, i => this.isOneChannelSubscribed(i.channels))
		this.overview.actors = this.filterChannels("actors", this.overview.actors, i => this.isOneChannelSubscribed(i.channels))

		this.overview.movies = this.filterChannels("movies", this.overview.movies, i => this.isChannelSubscribed(i.event?.channelId))
		this.overview.sports = this.filterChannels("sports", this.overview.sports, i => this.isSearchMatchSubscribed(i))
		//this.overview.programs = this.filterChannels("programs", this.overview.programs, i => this.isChannelSubscribed(i.event?.channelId))
		this.overview.channels = this.filterChannels("channels", this.overview.channels, i => this.isChannelSubscribed(i.id))
		//console.debug("overview recordings", this.overview.recordings)
		// MTVW-1011: only show "Top Treffer" if the return contains at least 2 replay categories (without top treffer) 
		let nbItems = 0
		// 1st pass
		for (const item in this.overview) {
			if (this.overview[item]?.length) {
				let hasEvents = false
				nbItems++
				this.overview[item].showFilterPill = true
				/*
				if (item === this.CATEGORY_RECORDINGS) {
					for (let i = 0; i < this.overview[item].length; i++) {
						if (this.overview[item][i].type !== "series") hasEvents = true
						console.debug("item=", item, this.overview[item][i].type)
					}
					if (hasEvents) {
						nbItems++
						this.overview[item].showFilterPill = true
					}
					else this.overview[item].showFilterPill = false
				}
				else {
					nbItems++
					this.overview[item].showFilterPill = true
				}
				*/
			}
			else this.overview[item].showFilterPill = false
		}
		// MTVW-1011: show 'Top' again, disable following code
		/*
		let limit = this.overview[this.CATEGORY_TOP].showFilterPill ? 3 : 2
		if (this.overview[this.CATEGORY_RECORDINGS].showFilterPill) limit++
		if (nbItems - limit < 0) this.overview[this.CATEGORY_TOP].showFilterPill = false
		else this.overview[this.CATEGORY_TOP].showFilterPill = this.overview[this.CATEGORY_TOP]?.length > 0
		// search issue when searching for "lost"
		if (nbItems === 1 && !this.overview[this.CATEGORY_TOP].showFilterPill) this.overview[this.CATEGORY_TOP].showFilterPill = true
		console.debug("fixupOverview", limit, nbItems, nbItems - limit < 0, this.overview)
		*/

		// 2nd pass
		//console.debug("firstItem showFilterPill showFilterPill", this.categorySelected, this.overview[this.categorySelected].showFilterPill)
		// MTVW-1076: stay on selected filter tab, if there are no entries select the first tab witth entries
		if (!this.overview?.[this.categorySelected]?.showFilterPill) {
			for (const item in this.filterLabel) {
				//console.debug("filterLabel", item, this.overview[item].showFilterPill, this.filterLabel)
				if (this.overview?.[item]?.showFilterPill) {
					//console.debug("firstItem", item, this.categorySelected)
					this.setCategorySelected(item)
					break
				}
			}
		}
		if (this.overview?.top?.length === 0) {
			console.debug("NO TOP RESULTS", this.results)
			//this.overview = {}
		}
		else {
			// generate periodic refresh
			this._intervalId = setInterval(() => {
				this.setRefresh(true)
			}, 60 * 1000)
		}
		// force update by toggling refresh
		this.setRefresh(false)
		this.setRefresh(true)
		console.debug("OVERVIEW %o", this.overview)
	}

	prevRecordings = []
	searchInProgress = null
	clearSearch() {
		this.prevRecordings = []
	}

	searchOverview = flow(function* (query, includeAdult = false, lite = true) {
		this.searchInProgress = query
		if (this._intervalId) clearInterval(this._intervalId)
		this._intervalId = null
		//this.overview = null
		let recordings = null
		try {
			const result = yield rootStore.searchService.overviewAsync(query, includeAdult, lite)
			if (query !== this.searchInProgress) {
				console.debug("overview_ current %s skipping %s", this.searchInProgress, query)
				return
			}
			console.debug("overview_ current %s accepted %s", this.searchInProgress, query)
			// MTVW-1014: sort CoCo object
			let sorted = {}
			for (const item in this.filterLabel) {
				if (result[item]) {
					sorted[item] = result[item]
				}
			}
			if (lite) {
				// MTVW-1011, MTVW-1016: prevent disappearing of recordings pill
				sorted.recordings = this.prevRecordings
				console.debug("overview_recordings lite=%s, %o", lite, sorted.recordings)
			}
			else {
				// MTVW-1011, MTVW-1016: prevent disappearing of recordings pill
				if (result?.recordings?.length > 0) {
					this.prevRecordings = [{data: {ignore: true}}]
					console.debug("overview_recordings lite=%s, %o", lite, this.prevRecordings)
				}
				else this.prevRecordings = []
			}
			this.overview = sorted
			console.debug("searchOverview", this.overview)
			if (!this.overview) return
		}
		catch (e) {
			console.error("searchOverview", e)
			return
		}

		this.fixupOverview()
	})

	// use isSubscribed(channelId) in the UI to filter channels
	infoActor = flow(function* (query, updatePageResult = true) {
		if (updatePageResult) this.actor = null
		let result = null
		try {
			result = yield rootStore.searchService.infoActorAsync(query)
		}
		catch (e) {
			console.error("infoActor", e)
		}

		// filter not subcribed channels
		// if result is null no need to be filtered
		if (result?.contents) {
			result.contents = this.filterChannels("infoActor", result.contents, i => {
				return this.isSearchMatchSubscribed(i)
			})
		}
		if (!updatePageResult) return result
		this.actor = result
		//console.debug("actor is", this.actor)
		// TODO: refresh?
		console.debug("this.actor", this.actor, this.actor?.person?.genres)
		return this.actor
	})

	// use isSubscribed(channelId) in the UI to filter channels
	infoGenre = flow(function* (query, updatePageResult = true) {
		if (updatePageResult) this.genre = null
		let result = null
		try {
			result = yield rootStore.searchService.infoGenreAsync(query)
		}
		catch (e) {
			console.error("infoGenre", e)
		}

		// filter not subcribed channels
		console.debug("infoGenre=", result)
		//this.genre = result
		if (result?.contents) {
			result.contents = this.filterChannels("infoGenre", result.contents, i => {
				/*
				const filtered = this.isSearchMatchSubscribed(i)
				this.genre = filtered
				return filtered
				*/
				return this.isSearchMatchSubscribed(i)
			})
		}
		// TODO: refresh?
		if (!updatePageResult) return result
		this.genre = result
		//console.debug("genre is", this.genre)
		return this.genre
	})

	// use isSubscribed(channelId) in the UI to filter channels
	searchActorInGenre = flow(function* (actor, genre, updatePageResult = true) {
		if (updatePageResult) this.actorInGenre = null
		let result = null
		try {
			result = yield rootStore.searchService.searchActorInGenreAsync(actor, genre)
			/*
			result = {contents: []}
			this.actor?.contents?.forEach(item => {
				console.debug("type==", item.type, item.data?.content?.title)
				let genres = null
				if (item.type === "series") genres = item.data?.genres
				else if (item.type === "event") genres = item.data?.content.genres
				if (genres.includes(genre)) {
					result.contents.push(item)
					//console.debug("HIT", genre, result)
				}
			})
			*/
		}
		catch (e) {
			console.error("searchActorInGenre", e)
		}

		if (result?.contents) {
			result.contents = this.filterChannels("infoGenre", result.contents, i => {
				return this.isSearchMatchSubscribed(i)
			})
		}
		// TODO: refresh?
		if (!updatePageResult) return result
		this.actorInGenre = result
		//console.debug("actorInGenre is", this.actorInGenre)
		return this.actorInGenre
	})

	// use isSubscribed(channelId) in the UI to filter channels
	searchGenreInGenre = flow(function* (genre, subGenre, updatePageResult = true) {
		if (updatePageResult) this.genreInGenre = null
		let result = null
		console.debug("searchGenreInGenre", genre, subGenre)
		try {
			result = yield rootStore.searchService.searchGenreInGenreAsync(genre, subGenre)
			//result = yield rootStore.searchService.infoGenreAsync(`${genre}?fmt=condensed&genre=${subGenre}`)
			//console.debug("genre= MS", genre, subGenre, result.contents.length, this.genre, this.genre.contents.length)
			/*
			result = {contents: []}
			this.genre?.contents?.forEach(item => {
				//console.debug("type==", item.type, item.data?.content?.title)
				let genres = null
				if (item.type === "series") genres = item.data?.genres
				else if (item.type === "event") genres = item.data?.content.genres
				if (genres.includes(genre)) {
					result.contents.push(item)
					//console.debug("HIT", genre, result)
				}
			})
			*/
		}
		catch (e) {
			console.error("searchGenreInGenre", e)
		}

		if (result?.contents) {
			result.contents = this.filterChannels("infoGenre", result.contents, i => {
				return this.isSearchMatchSubscribed(i)
			})
		}

		// TODO: refresh?
		if (!updatePageResult) return result
		this.genreInGenre = result
		console.debug("genreInGenre is", this.genreInGenre)
		return this.genreInGenre
	})

	// use isSubscribed(channelId) in the UI to filter channels
	getSeries = flow(function* (seriesId) {
		// CoCo might throttle the request sometimes (why?), return previous result in this case
		//this.series = null
		let result = null
		try {
			if (seriesId) result = yield rootStore.searchService.seriesEpisodesAsync(seriesId)
			//console.debug("getSeries res %o", result)
			// filter not subcribed channels
			let hasEpisodes = false
			console.debug("getSeries %o, %o, %o, %o", result.seasons?.length, result, result?.seasons?.[0]?.episodes?.length, result?.seasons?.[0]?.episodes)
			if (result?.seasons) {
				result.seasons.forEach(season => {
					const filtered = this.filterChannels("episodes", season.episodes, episode => {
						//console.debug("channel", episode.event.channelId, this.isChannelSubscribed(episode.event.channelId))
						return this.isChannelSubscribed(episode.event.channelId)
					})
					season.episodes = filtered
					hasEpisodes = hasEpisodes || filtered?.length > 0
				})
				if (!hasEpisodes) result.seasons = []
			}
			//console.debug("getSeries %o, %o, %o, %o", result.seasons?.length, result, result?.seasons?.[0]?.episodes?.length, result?.seasons?.[0]?.episodes)
			// TODO: refresh?
			this.series = result
			//console.debug("getSeries %o, %o, %o, %o", this.series?.seasons?.length, this.series, this.series?.seasons?.[0]?.episodes?.length, this.series?.seasons?.[0]?.episodes)
		}
		catch (e) {
			console.error("getSeries", e)
		}
		return this.series
	})

	// MTVW-1016: can be removed, not called anymore from handleShowDetails
	getSeriesEpisodes = flow(function* (seriesId) {
		// CoCo might throttle the request sometimes (why?), return previous result in this case
		let result = null
		//if (seriesId) result = yield rootStore.searchService.seriesEpisodesAsync(seriesId)
		if (seriesId) result = yield this.getSeries(seriesId)
	})

	removeFromArray(arr, what) {
		//console.debug("arr %o, what %s", arr, what)
		for (let i = 0; i < arr?.length; i++) {
			if (arr[i].toLowerCase() === what.toLowerCase()) {
				//console.debug("remove %o", arr[i])
				arr.splice(i, 1)
				i--
			}
		}
	}

	addToRecentSearch(searchText) {
		//console.debug("searchText", searchText)
		const MAX_RECENTS = 5
		let recent = [...rootStore.sso.browserStore.getRecentSearches()]
		if (!recent) recent = []
		const lValue = searchText
		// MTVW-617: Line below commented. Don't remove lastSearch but only exact match.
		//this.removeFromArray(recent, rootStore.sso.lastSearch)
		if (lValue?.length > 1) this.removeFromArray(recent, lValue.substring(0, lValue.length - 1))
		// remove if already existing, could lead to MAX_RECENTS-1 entries
		this.removeFromArray(recent, lValue)
		// remove oldest at the bottom
		if (recent?.length >= MAX_RECENTS) this.removeFromArray(recent, recent[recent.length - 1])
		// insert at top
		if (lValue?.length > 0) recent.unshift(lValue)
		rootStore.sso.browserStore.setRecentSearches(recent)

		rootStore.sso.lastSearch = searchText
	}

	recentSearches() {
		return rootStore.sso.browserStore.getRecentSearches()
	}

	_initAsync = flow(function* () {
	})

	_onMount() {
		// console.debug("search _onMount")
		if (this._intervalId) clearInterval(this._intervalId)
		this._intervalId = null
		this.refesh = false
		this.results = null
	}

	_onUnmount() {
		// console.debug("search _onUnmount")
		if (this._intervalId) clearInterval(this._intervalId)
		this._intervalId = null
		this.refesh = false
		this.results = null
	}

	_init() {
		this._initAsync()
	}
}
